/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import cx from 'classnames';
import {MouseEventHandler, memo, useCallback, useMemo, useRef, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {SET_UPSELL_CONFIRMED} from '~/CreateFabricInvitation/reducers/modals';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {
  getEventId,
  selectEventStatus,
  selectTemplateName,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {EVENT_STATUS_DRAFT} from '~/common/utils/event_constants';
import type {UpsellTrackingData} from '~/common/utils/upsellTracking';
import {trackUpsell} from '~/common/utils/upsellTracking';
import {showPremiumUpgradeSuccessToast} from '~/OneStepCreateInvitation/hooks/usePremiumFeatureBlocker';
import {Button} from '~library/atoms/Button';
import type {TypographyVariant} from '~library/atoms/Typography';
import {Typography} from '~library/atoms/Typography';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {Accordion} from '~library/molecules/Accordion';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {sendTrackingData} from '~library/utils/tracking';

import './UpsellModal.sass';
import './HostGalleryUpsellModal.sass';

type UpsellContentProps = {modalMode: (typeof UPSELL_MODES)[keyof typeof UPSELL_MODES]};
type Step1SubtitleProps = {className: string};
type ModalDetailsProps = {
  className?: string;
  modalMode: (typeof UPSELL_MODES)[keyof typeof UPSELL_MODES];
};

type UpsellModalContentProps = {
  containerDataQaId?: string;
  toggleUpsellModal: (boolean: boolean) => void;
  closeOverlay: () => void;
  noAutoRestore?: boolean;
  onConfirmAction: () => void;
  cancelDataQaId?: string;
  okDataQaId?: string;
  closeDataQaId?: string;
  upgradeToPremium?: () => void;
  mode?: (typeof UPSELL_MODES)[keyof typeof UPSELL_MODES];
  hideCloseButton?: boolean;
  hideSubtitle?: boolean;
  className?: string;
  cancelCtaText?: string;
  useRedux?: boolean;
  okCtaText?: string;
  headerVariant?: string;
  inlineHeader?: boolean;
  onDenyAction?: () => void;
  bannerImg?: string;
  accordionDetails?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  isSaving?: boolean;
};

const DIAMOND_RATIO = 1.14;

export const UPSELL_MODES = Object.freeze({
  STEP_1_COPY: 'step-1-copy',
  REMOVE_ADS: 'remove-ads',
  UNLOCK_DESIGN_TOOLS: 'unlock-design-tools',
  GIFTING: 'gifting',
  SHAREABLE_LINK: 'shareable-link',
  CUSTOM_SHAREABLE_LINK: 'custom-shareable-link',
  CURATED_SET: 'curated-set',
  HOST_PHOTO_GALLERY: 'hostPhotoGallery',
});

const HEADER_TEXT = {
  [UPSELL_MODES.STEP_1_COPY]: {
    title: "You've got a real designer's eye!",
    // Experiment name: "test_halloween_pricing" cleanup ticket: EVT-35806 '*Premium Invitation packages start at $17.99.',

    // Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
    subtitle: null,
    variantTitle: "You've got a real designer's eye!",
    // TODO: if test passes, replace subtitle as content
    content: null,
    desktopVideo:
      'https://g0.evitecdn.com/create-flow/upsell/Premium_Upsell_Step4_Marketing_Creative_Composite_Image_MW.mp4',
    mwVideo:
      'https://g0.evitecdn.com/create-flow/upsell/Premium_Upsell_Step4_Marketing_Creative_Composite_Image_Desktop.mp4',
  },
  [UPSELL_MODES.REMOVE_ADS]: {
    title: 'Upgrade to remove ads!',

    // Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
    subtitle: null,
    variantTitle: `Let's make it ad-free`,
    content:
      'None of the fluff, all of the good stuff. Remove ads now and let your Invitation shine.',
    desktopVideo:
      'https://g0.evitecdn.com/create-flow/upsell/02_PremiumUpsell_RemoveAds_3x_Desktop.mp4',
    mwVideo: 'https://g0.evitecdn.com/create-flow/upsell/02_PremiumUpsell_RemoveAds_3x_Mobile .mp4',
  },
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: {
    title: 'Make your invitation ✨pop✨',
    subtitle: null,
    variantTitle: null,
    content: null,
    desktopVideo: null,
    mwVideo: null,
  },
  [UPSELL_MODES.GIFTING]: {
    title: 'Upgrade to remove gift ideas',

    // Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
    subtitle: null,
    variantTitle: `Say "goodbye" to gift guides`,
    content: `So, you're not feeling any extra stuff — we've got you. Go Premium to remove Evite's curated gift guides.`,
    desktopVideo:
      'https://g0.evitecdn.com/create-flow/upsell/03_PremiumUpsell_RemoveGiftGuide_3x_Desktop.mp4',
    mwVideo:
      'https://g0.evitecdn.com/create-flow/upsell/03_PremiumUpsell_RemoveGiftGuide_3x_Mobile.mp4',
  },
  [UPSELL_MODES.SHAREABLE_LINK]: {
    title: 'Create a custom URL',
    subtitle: null,
    variantTitle: null,
    content: null,
    desktopVideo: null,
    mwVideo: null,
  },
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: {
    title: 'Customize your shareable link',
    subtitle: null,
    variantTitle: null,
    content: null,
    desktopVideo: null,
    mwVideo: null,
  },
  [UPSELL_MODES.CURATED_SET]: {
    title: 'Save time with this curated set!',
    subtitle: null,
    variantTitle: null,
    content: null,
    desktopVideo: null,
    mwVideo: null,
  },
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: {
    title: 'Curate your own host photo gallery',
    subtitle: 'Upload photos to a featured gallery on your Invitation.',

    // Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
    variantTitle: 'Make it personal!',
    content: 'Add a host photo gallery to display your favorite photos right in your Invitation.',
    desktopVideo:
      'https://g0.evitecdn.com/create-flow/upsell/04_PremiumUpsell_HostPhotoGallery_3x_Desktop.mp4',
    mwVideo:
      'https://g0.evitecdn.com/create-flow/upsell/04_PremiumUpsell_HostPhotoGallery_3x_Mobile.mp4',
  },
};

const BANNER_IMG = {
  [UPSELL_MODES.STEP_1_COPY]: {
    url: '',
  },
  [UPSELL_MODES.REMOVE_ADS]: {
    url: '',
  },
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: {
    url: '',
  },
  [UPSELL_MODES.GIFTING]: {
    url: '',
  },
  [UPSELL_MODES.SHAREABLE_LINK]: {
    url: '',
  },
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: {
    url: '',
  },
  [UPSELL_MODES.CURATED_SET]: {
    url: '',
  },
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: {
    url: 'https://g0.evitecdn.com/create-flow/designs/upsell-modal-features/host-photo-gallery-upsell-banner.png',
  },
};

const GET_WITH_PREMIUM_POINTS = {
  [UPSELL_MODES.STEP_1_COPY]: [],
  [UPSELL_MODES.REMOVE_ADS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
  ],
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.GIFTING]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CURATED_SET]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: [
    'Backgrounds and animated effects',
    'Envelopes, stamps, and stickers',
    'Custom, shareable URL for your Invitation',
    'No ads!',
  ],
};

const isInvitationMaker = window.location.pathname.startsWith('/invitation-maker');

const UpsellContent = ({modalMode}: UpsellContentProps) => {
  return (
    <div>
      <Typography variant="paragraph2" color="pebble" className="upsell-modal__content-text">
        {HEADER_TEXT[modalMode].content}
      </Typography>
    </div>
  );
};

// Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
// TODO: if test passes, replace subtitle as content
const Step1Subtitle = ({className}: Step1SubtitleProps) => {
  return (
    <div>
      <Typography variant="paragraph2" color="pebble" className={className}>
        You've selected a Premium feature — that's a nice touch. Switch to Premium now to keep those
        special customizations.
      </Typography>
    </div>
  );
};

const ModalDetails = ({className = '', modalMode = UPSELL_MODES.REMOVE_ADS}: ModalDetailsProps) => {
  return (
    <div className={className}>
      {GET_WITH_PREMIUM_POINTS[modalMode].map((point) => (
        <div className="create-flow__upsell__feature" key={`feature_${point}`}>
          <DiamondIcon />
          <Typography variant="paragraph2">{point}</Typography>
        </div>
      ))}
      {/* Experiment name: "test_halloween_pricing" cleanup ticket: EVT-35806 */}
    </div>
  );
};

const MemoizedVideoAlphaChannel = memo(VideoAlphaChannel);

// TODO: Experiment name: "test_premium_upsell_content" cleanup ticket: EVT-36514
export const UpsellModalContentVariant = ({
  containerDataQaId = 'upsell-modal',
  toggleUpsellModal,
  closeOverlay,
  noAutoRestore,
  onConfirmAction,
  cancelDataQaId,
  okDataQaId,
  closeDataQaId,
  upgradeToPremium,
  mode = 'gifting',
  hideCloseButton = false,
  cancelCtaText,
  useRedux = true,
  okCtaText,
  headerVariant = 'header4',
  inlineHeader = false,
  onDenyAction,
  bannerImg = '',
  accordionDetails = false,
  isSaving = false,
  ref,
}: UpsellModalContentProps) => {
  const templateName = useSelector(selectTemplateName);
  const eventId = useSelector(getEventId);
  const eventStatus = useSelector(selectEventStatus) || EVENT_STATUS_DRAFT;
  let store = null;
  if (useRedux) {
    store = useStore();
  }

  const [upsellIsSubmitting, setUpsellIsSubmitting] = useState(false);
  const isMediumAndUp = useIsMediumAndUp();
  const modalMode = mode in HEADER_TEXT ? mode : 'gifting';
  const isStep1 = modalMode === UPSELL_MODES.STEP_1_COPY;
  const headerVariantAlt = (isStep1 ? 'header5' : headerVariant) as TypographyVariant;
  const premiumUpsellOkCtaText = okCtaText ?? 'Go Premium';

  const mwVideo = HEADER_TEXT[modalMode].mwVideo || '';
  const desktopVideo = HEADER_TEXT[modalMode].desktopVideo || '';

  const premiumBadge = useMemo(
    () => (
      <div className="upsell-modal__static-badge">
        <DiamondIcon ratio={DIAMOND_RATIO} />
        <Typography variant="list1" color="premiumColor" inline>
          Premium
        </Typography>
      </div>
    ),
    []
  );

  const onUpgradeToPremium = useCallback(() => {
    let path;
    if (isInvitationMaker) {
      path = '/invitation-maker';
    } else if (window.location.pathname.startsWith('/event/')) {
      path = '/event';
    } else {
      path = window.location.pathname.replace(/\/$/, '').split('/').at(-1);
    }

    trackUpsell({
      path,
      feature: mode,
      status: eventStatus,
    } as UpsellTrackingData);

    sendTrackingData('cf_upsell_tracking', {
      identifier: `upsell_modal_${mode}`,
      templateId: templateName,
      eventId: eventId,
      status: eventStatus,
    });
    if (useRedux) {
      store?.dispatch({
        type: SET_UPSELL_CONFIRMED,
        payload: true,
      });
      store?.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {isPremium: true},
      });
    }
    showPremiumUpgradeSuccessToast();
    upgradeToPremium?.();
  }, [mode, store, useRedux, upgradeToPremium, templateName, eventStatus, eventId]);

  const handleCloseOverlay: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (isSaving && store) {
        store.dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {savingStep: false},
        });
      }
      if (closeOverlay) closeOverlay();
      else toggleUpsellModal(true);
    },
    [closeOverlay, isSaving, store, toggleUpsellModal]
  );

  return (
    <div
      className="create-flow__upsell-modal-content-variant"
      ref={ref}
      data-qa-id={containerDataQaId}
    >
      {
        <div className="create-flow__upsell-modal-variant-content-banner">
          <MemoizedVideoAlphaChannel
            srcVp9={isMediumAndUp ? desktopVideo : mwVideo}
            srcH265={isMediumAndUp ? desktopVideo : mwVideo}
            className="create-flow__upsell-modal-variant-banner-img"
            width="480"
            height="360"
          />

          {!isMediumAndUp &&
            (hideCloseButton ? null : (
              <Button
                variant="unstyled-button"
                className={
                  isMediumAndUp
                    ? 'create-flow__upsell-modal-content-close-btn'
                    : 'create-flow__upsell-modal-content-variant-close-btn'
                }
                data-qa-id={closeDataQaId || 'upsell-close'}
                onClick={closeOverlay ?? toggleUpsellModal}
                type="button"
                aria-label="Close modal"
                disabled={upsellIsSubmitting}
                aria-disabled={upsellIsSubmitting}
              >
                <CloseLineIcon color="white" ratio={0.75} />
              </Button>
            ))}
        </div>
      }
      <div className="create-flow__upsell-modal-content-variant-body">
        <div className="create-flow__upsell-content-variant-header-wrapper">
          {isMediumAndUp &&
            (hideCloseButton ? null : (
              <Button
                className="create-flow__upsell-modal-close-btn"
                variant="unstyled-button"
                data-qa-id={closeDataQaId || 'upsell-close'}
                onClick={handleCloseOverlay}
                type="button"
                aria-label="Close modal"
                disabled={upsellIsSubmitting}
                aria-disabled={upsellIsSubmitting}
              >
                <CloseLineIcon ratio={0.75} />
              </Button>
            ))}
          <div
            className={cx('upsell-modal__upsell-modal-content-variant-header', {
              inline: inlineHeader,
            })}
          >
            {premiumBadge}
            <Typography className="upsell-modal-content-variant-title" variant={headerVariantAlt}>
              {HEADER_TEXT[modalMode].variantTitle}
            </Typography>
            {!isStep1 && HEADER_TEXT[modalMode].content && <UpsellContent modalMode={modalMode} />}
            {isStep1 && (
              <Step1Subtitle className="upsell-modal__upsell-modal-content-variant-step1-text" />
            )}
          </div>
        </div>
        <div className="upsell-modal-content-variant-create-flow__upsell__ctas">
          <Button
            variant="primary"
            data-qa-id={okDataQaId || 'upsell-upgrade-now'}
            onClick={() => {
              setUpsellIsSubmitting(true);
              onUpgradeToPremium();
              onConfirmAction();
              toggleUpsellModal(false);
            }}
            size="large"
            noTextWrap
            disabled={upsellIsSubmitting}
          >
            {premiumUpsellOkCtaText}
          </Button>
          <Button
            variant="secondary"
            data-qa-id={cancelDataQaId || 'upsell-back'}
            className="upsell-stay-in-basic-variant"
            onClick={() => {
              if (onDenyAction) {
                onDenyAction();
                return closeOverlay();
              }

              if (!noAutoRestore && store) {
                store.dispatch({
                  type: UPDATE_GIFT_OPTIONS,
                  payload: {
                    options: {event_option_show_gifting: true},
                    showGifting: true,
                  },
                });
              }
              return closeOverlay();
            }}
            size="large"
            noTextWrap
            disabled={upsellIsSubmitting}
          >
            {cancelCtaText ?? 'Keep it free'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const UpsellModalContent = ({
  containerDataQaId = 'upsell-modal',
  toggleUpsellModal,
  closeOverlay,
  noAutoRestore,
  onConfirmAction,
  cancelDataQaId,
  okDataQaId,
  closeDataQaId,
  upgradeToPremium,
  mode = 'gifting',
  hideCloseButton = false,
  hideSubtitle = false,
  className,
  cancelCtaText,
  useRedux = true,
  okCtaText,
  headerVariant = 'header4',
  inlineHeader = false,
  onDenyAction,
  bannerImg = '',
  accordionDetails = false,
  isSaving = false,
  ref,
}: UpsellModalContentProps) => {
  const templateName = useSelector(selectTemplateName);
  const eventId = useSelector(getEventId);
  const eventStatus = useSelector(selectEventStatus) || EVENT_STATUS_DRAFT;

  let store = null;
  if (useRedux) {
    store = useStore();
  }

  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [upsellIsSubmitting, setUpsellIsSubmitting] = useState(false);
  const isMediumAndUp = useIsMediumAndUp();
  const modalMode = mode in HEADER_TEXT ? mode : 'gifting';
  const isStep1 = modalMode === UPSELL_MODES.STEP_1_COPY;
  const accordionRef = useRef<HTMLDivElement>(null);
  const headerVariantAlt = (isStep1 ? 'header5' : headerVariant) as TypographyVariant;
  const premiumUpsellOkCtaText = okCtaText ?? 'Switch to Premium';

  const onUpgradeToPremium = useCallback(() => {
    let path;
    if (isInvitationMaker) {
      path = '/invitation-maker';
    } else if (window.location.pathname.startsWith('/event/')) {
      path = '/event';
    } else {
      path = window.location.pathname.replace(/\/$/, '').split('/').at(-1);
    }

    trackUpsell({
      path,
      feature: mode,
      status: eventStatus,
    } as UpsellTrackingData);

    sendTrackingData('cf_upsell_tracking', {
      identifier: `upsell_modal_${mode}`,
      templateId: templateName,
      eventId: eventId,
      status: eventStatus,
    });
    if (useRedux) {
      store?.dispatch({
        type: SET_UPSELL_CONFIRMED,
        payload: true,
      });
      store?.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {isPremium: true},
      });
    }
    showPremiumUpgradeSuccessToast();
    upgradeToPremium?.();
  }, [mode, store, useRedux, upgradeToPremium, templateName, eventStatus, eventId]);

  const premiumBadge = useMemo(
    () =>
      isStep1 ? (
        <div className="upsell-modal__static-badge">
          <DiamondIcon ratio={DIAMOND_RATIO} />
          <Typography variant="list1" color="premiumColor" inline>
            Premium
          </Typography>
        </div>
      ) : (
        <div>
          <img
            src="https://g0.evitecdn.com/marketing/assets/PremiumDiamondAni.gif"
            alt=""
            width={142}
            height={108}
            className="create-flow__upsell__diamond-ani"
          />
          <Typography variant={headerVariantAlt} color="premiumColor" inline>
            Premium
          </Typography>
        </div>
      ),
    [isStep1, headerVariantAlt]
  );

  const handleCloseOverlay: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (isSaving && store) {
        store.dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {savingStep: false},
        });
      }
      if (closeOverlay) closeOverlay();
      else toggleUpsellModal(true);
    },
    [closeOverlay, isSaving, store, toggleUpsellModal]
  );

  return (
    <div
      className={cx('create-flow__upsell-modal-content', `upsell-modal__${modalMode}`, className)}
      ref={ref}
      data-qa-id={containerDataQaId}
    >
      <div className="create-flow__upsell__content">
        {hideCloseButton ? null : (
          <Button
            variant="unstyled-button"
            className="create-flow__upsell-modal-close-btn"
            data-qa-id={closeDataQaId || 'upsell-close'}
            onClick={handleCloseOverlay}
            type="button"
            aria-label="Close modal"
            disabled={upsellIsSubmitting}
            aria-disabled={upsellIsSubmitting}
          >
            <CloseLineIcon ratio={0.75} />
          </Button>
        )}
        <div className={cx('create-flow__upsell__header', {inline: inlineHeader})}>
          {premiumBadge}
          <Typography className="create-flow__upsell__title" variant={headerVariantAlt}>
            {HEADER_TEXT[modalMode].title}
          </Typography>
          {isStep1 && <Step1Subtitle className="upsell-modal__step1-text" />}
          {HEADER_TEXT[modalMode].subtitle && !hideSubtitle && (
            <Typography variant="paragraph2">{HEADER_TEXT[modalMode].subtitle}</Typography>
          )}
        </div>
        {bannerImg && (
          <img
            className="create-flow_upsell__banner-img"
            src={BANNER_IMG[modalMode].url}
            alt="host gallery example banner"
          />
        )}
        {accordionDetails ? (
          <Accordion
            accordionBodyWrapperId="upsell-modal-accordion-details"
            label="Explore all Premium features"
            labelVariant="paragraph2"
            chevron={<ChevronIcon direction={accordionExpanded ? 'up' : 'down'} ratio={0.75} />}
            onClose={() => setAccordionExpanded(false)}
            onOpen={() => setAccordionExpanded(true)}
            arrowPlacement="right"
            isOpen={accordionExpanded}
            ref={accordionRef}
            accordionQaId="upsell-modal-accordion-details-toggle"
          >
            <ModalDetails
              className="create-flow_upsell__feature-list-accordion"
              modalMode={modalMode}
            />
          </Accordion>
        ) : (
          <ModalDetails className="create-flow_upsell__feature-list" modalMode={modalMode} />
        )}
      </div>
      <div className="create-flow__upsell__ctas">
        <Button
          variant="primary"
          data-qa-id={okDataQaId || 'upsell-upgrade-now'}
          onClick={() => {
            setUpsellIsSubmitting(true);
            onUpgradeToPremium();
            onConfirmAction();
            toggleUpsellModal(false);
          }}
          size="large"
          noTextWrap
          disabled={upsellIsSubmitting}
        >
          {premiumUpsellOkCtaText}
        </Button>
        <Button
          variant={isMediumAndUp ? 'secondary' : 'unstyled-button'}
          data-qa-id={cancelDataQaId || 'upsell-back'}
          className="upsell-stay-in-basic"
          onClick={() => {
            if (onDenyAction) {
              onDenyAction();
              return closeOverlay();
            }

            if (!noAutoRestore && store) {
              store.dispatch({
                type: UPDATE_GIFT_OPTIONS,
                payload: {
                  options: {event_option_show_gifting: true},
                  showGifting: true,
                },
              });
            }
            return closeOverlay();
          }}
          size="large"
          noTextWrap
          disabled={upsellIsSubmitting}
        >
          {(isStep1 ? 'Keep it free' : cancelCtaText) || 'Stay in basic'}
        </Button>
      </div>
    </div>
  );
};
